import { GlobalConfigType, setLoanGroupData } from 'actions'
import cloneDeep from 'clone-deep'
import { type InputType, FieldOrigin, IVisibleProp, LoanFieldProps } from 'config'
import {
  calcCLTVFields,
  calcLTVFields,
  convertValueToLabel,
  dscrRelatedFields,
  editableOptionFields,
  fieldsByTransactionTypeAndProductTypeConstant,
  loanCustomRangeOptionFields,
  loanCustomSelectOptionFields,
  loanOptionsConstants,
  MINVALUE,
  MultiPropertyFields,
  priceLoanRelatedFields,
} from 'config/loan.constants'
import { isCustomFieldKey } from 'config/loan.input.fields.constants'
import { DateTime } from 'luxon'
import type {
  ILoanGlobalSettings,
  IManualPricingSetting,
  IManualProgram,
  IMinMaxLimitPerTypes,
} from 'pages/Admin/AdminTools/Configuration/GlobalSettings'
import { convertSelectRangeOptionsToMap } from 'pages/Admin/AdminTools/Configuration/LoanFieldsProperties/utils'
import {
  closingCostEstimateIsSameFeeWorksheet,
  isSameStructureApplicationCreditScore,
} from 'pages/LoanApplication/CreditScore/logic'
import { IRange } from 'pages/Resources'
import { IUnderwritingFee } from 'pages/Resources/UnderwritingFee'
import { toast } from 'react-toastify'
import { store } from 'reducers'
import { combinedMonthlyExpense, combinedPropertyMonthlyExpense } from 'services/pdfs/utils'
import {
  fromToNumberOptions,
  getPrice2decimal,
  getPrice3decimal,
  InputConvert,
  InputValidate,
  isBeforeOrEqualToday,
  isEmpty,
  removeComma,
  REQUIRED_FIELD_ERROR_MESSAGE,
  thousandSeperator,
} from 'utils'
import { removeInvisibleFields } from 'utils/loan'

import type {
  ILoan,
  ILoanDetail,
  ILoanDetailChangedJson,
  ILoanProcess,
  ILockDay,
  ILockTermPrice,
  IPrice,
  IPrice_Program_Price,
  IProduct,
  IProgram,
  IPropertyChangedJson,
} from './interfaces'

export const analyzeLoanStructureValue = (loan: ILoan, isSummary = true) => {
  const visibleFields = visibleLoansLogic(loan)
  let propertyPurchasePrice = removeComma(loan.propertyPurchasePrice)
  let asIsValue = removeComma(loan.asIsValue)
  let afterRepairValue = removeComma(loan.afterRepairValue)
  let rehabBudget = removeComma(loan.rehabBudget)
  let constructionReserve = removeComma(loan.constructionReserve)
  let proposedLoanAmount = removeComma(loan.proposedLoanAmount)
  let aPieceAmount = removeComma(loan.aPieceAmount)
  let lienPayoff = removeComma(loan.lienPayoff)
  let closingCostEstimate = removeComma(loan.closingCostEstimate)
  let interestReserve = removeComma(loan.interestReserve)
  let secondLien = removeComma(loan.secondLien)
  let secondLienPayment = 0
  if (removeComma(loan.secondLien) > 0) secondLienPayment = removeComma(loan.secondLienPayment)
  let proposedMonthlyInsurance = removeComma(loan.proposedMonthlyInsurance)
  let proposedMonthlyTaxes = removeComma(loan.proposedMonthlyTaxes)
  let proposedMonthlyHoaDues = removeComma(loan.proposedMonthlyHoaDues)
  let proposedMonthlyRent = removeComma(loan.proposedMonthlyRent)
  let floodInsurance = removeComma(loan.floodInsurance)
  let schoolTax = removeComma(loan.schoolTax)
  let otherTax = removeComma(loan.otherTax)

  if (visibleFields.includes('isBPiece') && loan.isBPiece) proposedLoanAmount = aPieceAmount

  if (isSummary && loan.isMultipleProperties) {
    const { multipleProperties } = loan
    multipleProperties?.forEach((item) => {
      propertyPurchasePrice += removeComma(item.propertyPurchasePrice)
      asIsValue += removeComma(item.asIsValue)
      afterRepairValue += removeComma(item?.afterRepairValue)
      rehabBudget += removeComma(item?.rehabBudget)
      constructionReserve += removeComma(item?.constructionReserve)
      lienPayoff += removeComma(item.lienPayoff)
      secondLien += removeComma(item.secondLien)
      if (removeComma(item.secondLien) > 0) secondLienPayment += removeComma(item.secondLienPayment)
      proposedMonthlyInsurance += removeComma(item.proposedMonthlyInsurance)
      proposedMonthlyTaxes += removeComma(item.proposedMonthlyTaxes)
      proposedMonthlyHoaDues += removeComma(item.proposedMonthlyHoaDues)
      proposedMonthlyRent += removeComma(item.proposedMonthlyRent)
      floodInsurance += removeComma(item.floodInsurance)
      schoolTax += removeComma(item.schoolTax)
      otherTax += removeComma(item.otherTax)
    })
  }

  if (!visibleFields.includes('propertyPurchasePrice')) propertyPurchasePrice = 0
  if (!visibleFields.includes('asIsValue')) asIsValue = 0
  if (!visibleFields.includes('constructionReserve')) constructionReserve = 0
  if (!visibleFields.includes('proposedLoanAmount')) proposedLoanAmount = 0
  if (!visibleFields.includes('afterRepairValue')) afterRepairValue = 0
  if (!visibleFields.includes('rehabBudget')) rehabBudget = 0
  if (!visibleFields.includes('lienPayoff')) lienPayoff = 0
  if (!visibleFields.includes('closingCostEstimate')) closingCostEstimate = 0
  if (!visibleFields.includes('interestReserve')) interestReserve = 0
  if (!visibleFields.includes('secondLien')) secondLien = 0

  let minPurchasePriceAppraisedValue = asIsValue
  if (loan.transactionType === 'Purchase') {
    if (asIsValue === 0) minPurchasePriceAppraisedValue = propertyPurchasePrice
    else if (propertyPurchasePrice === 0) minPurchasePriceAppraisedValue = asIsValue
    else minPurchasePriceAppraisedValue = Math.min(propertyPurchasePrice, asIsValue)
  }
  return {
    propertyPurchasePrice,
    asIsValue,
    proposedLoanAmount,
    afterRepairValue,
    rehabBudget,
    constructionReserve,
    lienPayoff,
    closingCostEstimate,
    interestReserve,
    secondLien,
    secondLienPayment,
    proposedMonthlyInsurance,
    proposedMonthlyTaxes,
    proposedMonthlyHoaDues,
    proposedMonthlyRent,
    floodInsurance,
    schoolTax,
    otherTax,
    minPurchasePriceAppraisedValue,
  }
}

export const getLockExpired = (data: any) => {
  let rlt = false

  if (data.rateLocked && data.expireDate) {
    const expireDate = DateTime.fromJSDate(new Date(data.expireDate)).startOf('day')

    if (expireDate.isValid) {
      // const state = store.getState()
      // const { type } = state.loan as ILoan
      // const { loanStatus } = state.loanDetail as ILoanDetail
      // const secondaryMarketingConfig = state.globalConfigReducer[GlobalConfigType.SecondaryMarketing]

      rlt = !isBeforeOrEqualToday(data.expireDate)

      // const loanStatusForLockExpWarning = secondaryMarketingConfig?.rules?.lockExpWarning?.[type]
      // if (loanStatusForLockExpWarning && rlt && !loanStatusForLockExpWarning.includes(loanStatus)) rlt = false
    } else {
      console.error('Invalid expireDate:', data.expireDate)
    }
  }

  return rlt
}

export const calculateLoanLimits = (
  loan: ILoan | null = null,
  getMaximumLoanAmount: boolean = false,
  maxLimit: any = null,
) => {
  let rlt: any = {
    Limit: {},
  }
  try {
    const state = store.getState()
    let ltvMaxLimit: any = state.ltvMaxLimit
    if (maxLimit !== null) ltvMaxLimit = maxLimit

    if (loan === null) {
      loan = state.loan as ILoan
    }
    const { productType, transactionType } = loan

    let _aiv_ltv = 0
    let _arv_ltv = 0
    let _ltc = 0
    let _ltp = 0

    const visibleFields = fieldsByTransactionTypeAndProductTypeConstant[productType]?.[transactionType] || []

    const {
      propertyPurchasePrice,
      constructionReserve,
      proposedLoanAmount,
      afterRepairValue,
      rehabBudget,
      lienPayoff,
      closingCostEstimate,
      interestReserve,
      minPurchasePriceAppraisedValue,
    } = analyzeLoanStructureValue(loan)

    _aiv_ltv = (100 * (proposedLoanAmount - constructionReserve)) / minPurchasePriceAppraisedValue
    _arv_ltv = (100 * proposedLoanAmount) / afterRepairValue
    _ltc = (100 * proposedLoanAmount) / (minPurchasePriceAppraisedValue + rehabBudget)
    _ltp = (100 * (proposedLoanAmount - constructionReserve)) / propertyPurchasePrice

    _aiv_ltv = Number(_aiv_ltv.toFixed(3))
    _arv_ltv = Number(_arv_ltv.toFixed(3))
    _ltc = Number(_ltc.toFixed(3))
    _ltp = Number(_ltp.toFixed(3))

    rlt.Limit.aiv_ltv = _aiv_ltv
    rlt.Limit.arv_ltv = _arv_ltv
    rlt.Limit.ltc = _ltc
    rlt.Limit.ltp = _ltp

    // Calculate B Piece limits if applicable
    if (visibleFields.includes('isBPiece') && loan.isBPiece) {
      const totalLoanAmount = removeComma(loan.proposedLoanAmount)

      let _b_piece_aiv_ltv = (100 * (totalLoanAmount - constructionReserve)) / minPurchasePriceAppraisedValue
      let _b_piece_arv_ltv = (100 * totalLoanAmount) / afterRepairValue
      let _b_piece_ltc = (100 * totalLoanAmount) / (minPurchasePriceAppraisedValue + rehabBudget)
      let _b_piece_ltp = (100 * (totalLoanAmount - constructionReserve)) / propertyPurchasePrice

      rlt.Limit.b_piece_aiv_ltv = Number(_b_piece_aiv_ltv.toFixed(3))
      rlt.Limit.b_piece_arv_ltv = Number(_b_piece_arv_ltv.toFixed(3))
      rlt.Limit.b_piece_ltc = Number(_b_piece_ltc.toFixed(3))
      rlt.Limit.b_piece_ltp = Number(_b_piece_ltp.toFixed(3))
    }

    try {
      rlt.Limit.max_aiv_ltv = ltvMaxLimit.max_aiv_ltv
      rlt.Limit.max_arv_ltv = ltvMaxLimit.max_arv_ltv
      rlt.Limit.max_ltc = ltvMaxLimit.max_ltc
      rlt.Limit.max_ltp = ltvMaxLimit.max_ltp
    } catch {}

    if (getMaximumLoanAmount) {
      let minValue = MINVALUE
      if (rlt.Limit.max_aiv_ltv) {
        minValue = Math.min(
          minValue,
          removeComma((rlt.Limit.max_aiv_ltv * minPurchasePriceAppraisedValue) / 100 + constructionReserve),
        )
      }
      if (rlt.Limit.max_arv_ltv) {
        minValue = Math.min(minValue, removeComma((rlt.Limit.max_arv_ltv * afterRepairValue) / 100))
      }
      if (rlt.Limit.max_ltc) {
        minValue = Math.min(
          minValue,
          removeComma((rlt.Limit.max_ltc * (minPurchasePriceAppraisedValue + rehabBudget)) / 100),
        )
      }
      if (rlt.Limit.max_ltp) {
        minValue = Math.min(
          minValue,
          removeComma((rlt.Limit.max_ltp * propertyPurchasePrice) / 100 + constructionReserve),
        )
      }
      if (transactionType === 'Refinance') {
        if (visibleFields.indexOf('rehabBudget') !== -1) {
          minValue = Math.min(
            minValue,
            rehabBudget + rehabBudget * 0.2 + lienPayoff + closingCostEstimate + interestReserve,
          )
        }
      }
      rlt.Limit = Math.trunc(minValue)
    }
  } catch {}
  return rlt.Limit
}

export const calculateLTV = (loan: ILoan | null = null, cltv: boolean = false, isBPiece: boolean = false) => {
  let rlt: any = ''
  try {
    if (loan === null) {
      const state = store.getState()
      loan = state.loan as ILoan
    }

    let { constructionReserve, proposedLoanAmount, secondLien, minPurchasePriceAppraisedValue } =
      analyzeLoanStructureValue(loan)
    if (isBPiece) proposedLoanAmount = removeComma(loan.proposedLoanAmount) - removeComma(loan.aPieceAmount)

    rlt = (100 * (proposedLoanAmount - constructionReserve)) / minPurchasePriceAppraisedValue
    if (cltv) {
      const totalLoanAmount = removeComma(loan.proposedLoanAmount)
      rlt = (100 * (totalLoanAmount + secondLien - constructionReserve)) / minPurchasePriceAppraisedValue
    }

    return rlt.toFixed(3)
  } catch {}
  return rlt
}

export const calculateBPiece = (loan: ILoan) => {
  return thousandSeperator(removeComma(loan.proposedLoanAmount) - removeComma(loan.aPieceAmount))
}

export const calculateLoanAmount = (loan: ILoan | null = null, LTV: number) => {
  let rlt: any = ''
  try {
    if (loan === null) {
      const state = store.getState()
      loan = state.loan as ILoan
    }

    const { constructionReserve, minPurchasePriceAppraisedValue } = analyzeLoanStructureValue(loan)

    rlt = (LTV * minPurchasePriceAppraisedValue) / 100 + constructionReserve

    return Math.floor(rlt)
  } catch {}
  return rlt
}

export const calculateLTC = (loan: ILoan | null = null) => {
  let rlt: any = ''
  try {
    if (loan === null) {
      const state = store.getState()
      loan = state.loan as ILoan
    }

    const { minPurchasePriceAppraisedValue, rehabBudget } = analyzeLoanStructureValue(loan)
    const totalLoanAmount = removeComma(loan.proposedLoanAmount)

    rlt = (100 * totalLoanAmount) / (minPurchasePriceAppraisedValue + rehabBudget)

    return rlt.toFixed(3)
  } catch {}
  return rlt
}

export const calculateLoanAmountFromLTC = (loan: ILoan | null = null, LTC: number) => {
  let rlt: any = ''
  try {
    if (loan === null) {
      const state = store.getState()
      loan = state.loan as ILoan
    }

    const { minPurchasePriceAppraisedValue, rehabBudget } = analyzeLoanStructureValue(loan)

    rlt = (LTC * (minPurchasePriceAppraisedValue + rehabBudget)) / 100

    return Math.floor(rlt)
  } catch {}
  return rlt
}

export const visibleLoansLogic = (loan?: ILoan): string[] => {
  const state = store.getState()
  if (!loan) {
    loan = state.loan as ILoan
  }
  const { globalConfigReducer } = state
  const loanFieldProperties = globalConfigReducer[GlobalConfigType.LoanFields] as LoanFieldProps
  const fieldProps = loanFieldProperties[FieldOrigin.LoanStructure]
  let visibleFields = ['productType', 'transactionType']
  if (loan['productType'] && loan['transactionType']) {
    const definedVisibleFields =
      fieldsByTransactionTypeAndProductTypeConstant[loan['productType']]?.[loan['transactionType']] || []
    visibleFields = [...visibleFields, ...definedVisibleFields]
    if (loan['propertyType'] === 'Condo') visibleFields.push('condoType')
    if (loan['propertyState'] === 'NY' && loan['transactionType'] === 'Refinance')
      visibleFields.push('isCEMATransaction')
    if (removeComma(loan['secondLien']) > 0) visibleFields.push('secondLienPayment')
    const newVisibleInputs = Object.keys(fieldProps).filter((key) => {
      if (isCustomFieldKey(key)) return true
      const { visibleLogic } = fieldProps[key]
      if (visibleLogic && visibleLogic.length) return true
    })
    visibleFields.push(...newVisibleInputs)
  }
  visibleFields = removeInvisibleFields(loan, FieldOrigin.LoanStructure, visibleFields)
  return visibleFields
}

export const visiblePropertiesLogic = (index: number, loanData?: any): string[] => {
  const { loan, globalConfigReducer } = store.getState()
  if (!loanData) loanData = loan
  const propertyData = loanData.multipleProperties[index]
  const loanFieldProperties = globalConfigReducer[GlobalConfigType.LoanFields] as LoanFieldProps
  const fieldProps = loanFieldProperties[FieldOrigin.LoanStructure]
  let visibleFields: string[] = []
  if (loanData['productType'] && loanData['transactionType']) {
    const definedVisibleFields =
      fieldsByTransactionTypeAndProductTypeConstant[loanData['productType']]?.[loanData['transactionType']] || []
    visibleFields = [...visibleFields, ...definedVisibleFields]
    if (propertyData['propertyType'] === 'Condo') visibleFields.push('condoType')
    if (removeComma(propertyData['secondLien']) > 0) visibleFields.push('secondLienPayment')
    const newVisibleInputs = Object.keys(fieldProps).filter((key) => {
      if (isCustomFieldKey(key)) return true
      const { visibleLogic } = fieldProps[key]
      if (visibleLogic && visibleLogic.length) return true
    })
    visibleFields.push(...newVisibleInputs)
  }
  visibleFields = removeInvisibleFields(loanData, FieldOrigin.LoanStructure, visibleFields)
  return visibleFields
}

export const propertyTypeNumberUnitInputLogic = (propertyType: string, input: any) => {
  switch (propertyType) {
    case 'SFR-Detached':
    case 'SFR-Attached':
    case 'Industrial':
      input.inputType = 'text'
      input.value = 1
      input.readOnly = true
      break
    case 'PUD':
    case 'Automotive':
    case 'Modular':
    case 'Manufactured':
      input.inputType = 'select'
      input.options = fromToNumberOptions(1, 4)
      input.hasDefaultOption = true
      input.readOnly = false
      break
    case 'Office':
    case 'Warehouse':
      input.inputType = 'select'
      input.options = fromToNumberOptions(1, 100)
      input.hasDefaultOption = true
      input.readOnly = false
      break
    case '2-4 Units':
      input.inputType = 'select'
      input.options = fromToNumberOptions(2, 4)
      input.hasDefaultOption = true
      input.readOnly = false
      break

    default: {
      input.inputType = 'text'
      input.readOnly = false
      break
    }
  }
  return input
}

export const loanFieldsValidateAll = (
  fieldData: any,
  loanGlobalSettings: ILoanGlobalSettings,
  fieldProperties: Record<string, IVisibleProp>,
  isPool: boolean,
  loan?: any,
) => {
  const loanField = cloneDeep(fieldData)
  const { loan: loanData, loanDetail } = store.getState()
  if (!loan) loan = loanData

  Object.keys(fieldProperties)
    .filter((key) => isCustomFieldKey(key))
    .forEach((key) => {
      loanField[key] = fieldProperties[key] as any
    })
  loanField.numberOfUnits = propertyTypeNumberUnitInputLogic(loan.propertyType, loanField.numberOfUnits)
  const experienceRange = getRangeFromString(loan.experience)
  let { from, to } = experienceRange
  if (from === null) from = 0
  if (to === null) to = 100
  loanField.howManyExperiences.options = fromToNumberOptions(from, to)

  Object.keys(loanField).map((key) => {
    if (
      fieldProperties[key] &&
      !!fieldProperties[key]?.options &&
      !!fieldProperties[key].options?.length &&
      [...loanCustomRangeOptionFields, ...loanCustomSelectOptionFields].includes(key)
    ) {
      ;(loanField[key] as any).options = convertSelectRangeOptionsToMap(fieldProperties[key].options || [])
    }
    if (
      fieldProperties[key] &&
      !!fieldProperties[key]?.selectedOptions &&
      !!fieldProperties[key].selectedOptions?.length &&
      editableOptionFields.includes(key)
    ) {
      ;(loanField[key] as any).options = fieldProperties[key].selectedOptions
    }
    if (key === 'loanTerm') {
      if (!isNonDSCRProduct(loan.productType)) loanField[key].options = loanOptionsConstants.dscrLoanTerm
      else {
        const loanTermOption = convertSelectRangeOptionsToMap(fieldProperties[key].options)
        loanField[key].options = isEmpty(loanTermOption) ? loanOptionsConstants.loanTerm : loanTermOption
      }
    }

    loanField[key].title =
      fieldProperties[key] && fieldProperties[key].title ? fieldProperties[key].title || '' : loanField[key].title
    loanField[key].type = (
      fieldProperties[key] && fieldProperties[key].type ? fieldProperties[key].type : loanField[key].type
    ) as any

    if (fieldProperties?.[key]?.required !== undefined) loanField[key].required = fieldProperties[key].required

    loanField[key].error = InputValidate({ ...loanField[key], value: loan[key] }, loanDetail, loan)
  })

  //  The After Repair Value (ARV) must be no less than setting% of the total costs (lesser of the as is value / purchase price + rehab budget)
  let { afterRepairValue, rehabBudget, minPurchasePriceAppraisedValue } = analyzeLoanStructureValue(loan)
  const minAfterRepairValue = Math.ceil(
    ((100 + Number(loanGlobalSettings.minAfterRepairValue)) * (minPurchasePriceAppraisedValue + rehabBudget)) / 100,
  )
  if (0 < afterRepairValue && afterRepairValue < minAfterRepairValue) {
    loanField['afterRepairValue'].error = `The After Repair Value does not confirm at least ${
      loanGlobalSettings.minAfterRepairValue
    }% profitability due to it being less than $${getPrice2decimal(minAfterRepairValue)}`
  }

  const sameEstimate = closingCostEstimateIsSameFeeWorksheet()
  if (!sameEstimate.same) loanField.closingCostEstimate.error = sameEstimate.error

  const sameCredit = isSameStructureApplicationCreditScore()
  if (!sameCredit.same && sameCredit.value !== 0) loanField['estimatedCreditScore'].error = sameCredit.error

  if (isPool) loanField['closingCostEstimate'].readOnly = false

  return loanField
}

export const multiPropertyFieldsValidateAll = (
  index: number,
  loanField: any,
  fieldProperties: Record<string, IVisibleProp>,
  loanData?: ILoan,
) => {
  const { loan, loanDetail } = store.getState()
  if (!loanData) loanData = loan
  if (!loanData) return loanField
  const targetData = loanData.multipleProperties[index]
  loanField.numberOfUnits = propertyTypeNumberUnitInputLogic(targetData.propertyType, loanField.numberOfUnits)

  Object.keys(loanField).map((key) => {
    if (
      !!(targetData as any)[key] &&
      fieldProperties[key] &&
      !!fieldProperties[key]?.options &&
      !!fieldProperties[key].options?.length &&
      [...loanCustomRangeOptionFields, ...loanCustomSelectOptionFields].includes(key)
    ) {
      ;(loanField[key] as any).options = convertSelectRangeOptionsToMap(fieldProperties[key]?.options || [])
    }
    if (
      fieldProperties[key] &&
      !!fieldProperties[key]?.selectedOptions &&
      !!fieldProperties[key].selectedOptions?.length &&
      editableOptionFields.includes(key)
    ) {
      ;(loanField[key] as any).options = fieldProperties[key].selectedOptions
    }
    loanField[key].title =
      fieldProperties[key] && fieldProperties[key].title ? fieldProperties[key].title || '' : loanField[key].title
    loanField[key].type = (
      fieldProperties[key] && fieldProperties[key].type ? fieldProperties[key].type : loanField[key].type
    ) as any
    if (fieldProperties?.[key]?.required !== undefined) loanField[key].required = fieldProperties[key].required
    loanField[key].error = InputValidate({ ...loanField[key], value: (targetData as any)[key] }, loanDetail, loan)
  })

  return loanField
}

export const showLimitSection = () => {
  let flag = true
  const { loan } = store.getState()
  if (!isNonDSCRProduct(loan['productType'])) flag = false
  return flag
}

export const loanDataConvert = (fields: Record<string, InputType>) => {
  try {
    const { loan } = store.getState()
    const data: any = {}
    Object.keys(fields).map((key) => {
      data[key] = InputConvert(fields[key], loan[key])
    })
    store.dispatch(setLoanGroupData(data))
    const key2 = 'propertyCounty'
    data[key2] = loan[key2]
    return data
  } catch (e) {
    return {}
  }
}

export const getMaximumTotalLoanAmountTip = (loan: ILoan | null, limit: any) => {
  if (loan === null) {
    const state = store.getState()
    loan = state.loan as ILoan
  }
  const lists = []
  let visibleFields = fieldsByTransactionTypeAndProductTypeConstant[loan.productType]?.[loan.transactionType]
  if (visibleFields === undefined) visibleFields = []

  const {
    propertyPurchasePrice,
    constructionReserve,
    proposedLoanAmount,
    afterRepairValue,
    rehabBudget,
    lienPayoff,
    closingCostEstimate,
    interestReserve,
    minPurchasePriceAppraisedValue,
  } = analyzeLoanStructureValue(loan)

  let list: any = {}

  if (loan.transactionType === 'Refinance') {
    if (visibleFields.indexOf('rehabBudget') !== -1) {
      if (limit?.max_aiv_ltv || limit?.max_arv_ltv || limit?.max_ltc || limit?.max_ltp) {
        lists.push({
          label:
            'Construction Budget + 20% of Construction Budget + Lien Payoff + Closing Cost Estimate + Interest Reserve',
          value: rehabBudget + rehabBudget * 0.2 + lienPayoff + closingCostEstimate + interestReserve,
        })
      }
    }
  }

  let AIVLTVMIN = -1
  if (!isEmpty(limit?.max_aiv_ltv)) {
    const percent = limit.max_aiv_ltv / 100
    list = {
      label: `AIV-LTV MAX: Min(As Is Appraised Value, Property Purchase Price) x ${percent} + Construction Amount Financed`,
      value: minPurchasePriceAppraisedValue * percent + constructionReserve,
    }
    lists.push(list)
    AIVLTVMIN = minPurchasePriceAppraisedValue * percent
  }
  if (!isEmpty(limit?.max_arv_ltv)) {
    const percent = limit.max_arv_ltv / 100
    list = {
      label: `ARV-LTV MAX: After Repair Value x ${percent}`,
      value: afterRepairValue * percent,
    }
    lists.push(list)
  }
  if (!isEmpty(limit?.max_ltc)) {
    const percent = limit.max_ltc / 100
    list = {
      label: `LTC MAX: (Min(As Is Appraised Value, Property Purchase Price) + Construction Budget) x ${percent}`,
      value: (minPurchasePriceAppraisedValue + rehabBudget) * percent,
    }
    lists.push(list)
  }
  if (!isEmpty(limit?.max_ltp)) {
    const percent = limit.max_ltp / 100
    list = {
      label: `LTP MAX: Property Purchase Price x ${percent} + Construction Amount Financed`,
      value: propertyPurchasePrice * percent + constructionReserve,
    }
    lists.push(list)
  }

  let minValue = MINVALUE
  let element = ``
  lists.map((item) => {
    if (minValue > item.value) minValue = item.value
    element += `<div class="mb-1">${item.label} = <span class="text-[14px]">${getPrice3decimal(
      item.value,
    )}</span></div>`
  })

  const tooltip = `
    <div class="">
      <div class="font-bold mb-2">The minimum value of the following:</div>
      ${element}
    </div>
    `

  let constructionReserveMinwithoutChangeLoanAmountValidation = -1
  if (AIVLTVMIN !== -1) {
    if (minValue >= proposedLoanAmount) {
      constructionReserveMinwithoutChangeLoanAmountValidation = Math.max(0, minValue - AIVLTVMIN)
    }
    if (minValue - AIVLTVMIN >= 0 && minValue - AIVLTVMIN >= proposedLoanAmount - AIVLTVMIN) {
      constructionReserveMinwithoutChangeLoanAmountValidation = Math.max(0, proposedLoanAmount - AIVLTVMIN)
    }
  }

  return {
    tooltip: tooltip,
    minValue: Math.trunc(minValue),
    lists: lists.length,
    constructionReserveMinwithoutChangeLoanAmountValidation,
  }
}

export const isNonDSCRProduct = (productType: string) => {
  return 'DSCR (Long Term Rental)' !== productType
}

export const estimatedClosingAmountBorrower = (
  loan: ILoan,
  loanGlobalSettings: ILoanGlobalSettings,
  isSummary = true,
) => {
  let value = 0,
    valueWithoutConstructionCalc = 0,
    closingTooltip = '',
    closingMaxRehabBudget = 0,
    closingError = '',
    rehabFinancedPercent = '',
    totalRehabFinancedPercent = '',
    totalRehabFinancedTooltip = '',
    label = ''

  label = `Estimated Closing Amount ${loan.transactionType === 'Refinance' ? 'to' : 'from'} Borrower`

  const visibleFields = visibleLoansLogic(loan)

  const {
    proposedLoanAmount,
    closingCostEstimate,
    lienPayoff,
    interestReserve,
    constructionReserve,
    rehabBudget,
    propertyPurchasePrice,
  } = analyzeLoanStructureValue(loan, isSummary)

  if (loan.transactionType === 'Refinance') {
    valueWithoutConstructionCalc =
      removeComma(proposedLoanAmount) -
      removeComma(lienPayoff) -
      removeComma(closingCostEstimate) -
      removeComma(loan.escrowRepairHoldback) -
      removeComma(interestReserve)
    value = valueWithoutConstructionCalc - removeComma(constructionReserve)

    closingTooltip = `Proposed Loan Amount<br/>- Lien Payoff<br/>- Escrow Repair Holdback<br/>- Closing Cost Estimate<br/>- Interest Reserve<br/>- Construction Amount Financed`

    if (!visibleFields.includes('escrowRepairHoldback')) {
      value += removeComma(loan.escrowRepairHoldback)
      closingTooltip = closingTooltip.replace('<br/>- Escrow Repair Holdback', '')
    }
    if (!visibleFields.includes('lienPayoff')) {
      value += removeComma(lienPayoff)
      closingTooltip = closingTooltip.replace('<br/>- Lien Payoff', '')
    }
    if (!visibleFields.includes('constructionReserve')) {
      value += removeComma(constructionReserve)
      closingTooltip = closingTooltip.replace('<br/>- Construction Amount Financed', '')
    }
    if (!visibleFields.includes('interestReserve')) {
      value += removeComma(interestReserve)
      closingTooltip = closingTooltip.replace('<br/>- Interest Reserve', '')
    }

    if (visibleFields.includes('rehabBudget')) {
      closingMaxRehabBudget =
        (removeComma(rehabBudget) * removeComma(loanGlobalSettings.maxPercentOfEstimatedClosingAmountToBorrower)) / 100
      if (value > closingMaxRehabBudget) {
        closingError = `May not exceed ${
          loanGlobalSettings.maxPercentOfEstimatedClosingAmountToBorrower
        }% of Construction Budget which is $${getPrice3decimal(closingMaxRehabBudget)}`
      }
    }
    if (value < 0) label = label.replace('to', 'from')
  } else {
    value =
      removeComma(propertyPurchasePrice) -
      removeComma(proposedLoanAmount) +
      removeComma(loan.escrowRepairHoldback) +
      removeComma(closingCostEstimate) +
      removeComma(interestReserve) +
      removeComma(constructionReserve)
    closingTooltip =
      'Property Purchase Price<br/>- Proposed Loan Amount<br/>+ Escrow Repair Holdback<br/>+ Closing Cost Estimate<br/>+ Interest Reserve<br/>+ Construction Amount Financed'

    if (!visibleFields.includes('escrowRepairHoldback')) {
      value -= removeComma(loan.escrowRepairHoldback)
      closingTooltip = closingTooltip.replace('<br/>+ Escrow Repair Holdback', '')
    }
    if (!visibleFields.includes('constructionReserve')) {
      value -= removeComma(constructionReserve)
      closingTooltip = closingTooltip.replace('<br/>+ Construction Amount Financed', '')
    }
    if (!visibleFields.includes('interestReserve')) {
      value -= removeComma(interestReserve)
      closingTooltip = closingTooltip.replace('<br/>+ Interest Reserve', '')
    }
    if (value < 0) label = label.replace('from', 'to')
  }
  if (visibleFields.includes('rehabBudget') && removeComma(rehabBudget) > 0) {
    rehabFinancedPercent = ((removeComma(constructionReserve) * 100) / removeComma(rehabBudget)).toFixed(2)
    const closingCost = loan.transactionType === 'Refinance' ? (value < 0 ? 0 : value) : 0
    totalRehabFinancedTooltip = `'Construction Amount Financed'${
      closingCost > 0 ? " + 'Estimated Closing Amount to Borrower'" : ''
    }/<br/>'Construction Budget'`
    totalRehabFinancedPercent = (
      ((removeComma(closingCost) + removeComma(constructionReserve)) * 100) /
      removeComma(rehabBudget)
    ).toFixed(2)
  }

  return {
    rehabFinancedPercent,
    totalRehabFinancedPercent,
    totalRehabFinancedTooltip,
    value,
    label,
    valueWithoutConstructionCalc,
    closingTooltip,
    closingMaxRehabBudget,
    closingError,
    constructionReserve,
  }
}

export const validateLoanStructureData = (loan: ILoan, rateData: any, errorMessage: boolean = false) => {
  let errors = []
  const { auth } = store.getState()
  if (removeComma(rateData.rate) <= 0 && !auth.profile.isBorrower)
    errors.push(`Interest Rate & Base Price is Required!`)
  const sameEstimate = closingCostEstimateIsSameFeeWorksheet()
  if (!sameEstimate.same) errors.push(sameEstimate.error)
  const sameCredit = isSameStructureApplicationCreditScore()
  if (!sameCredit.same && sameCredit.value !== 0) errors.push(sameCredit.error)
  if (errorMessage && errors.length) toast(errors.join('\n'), { type: 'error' })
  return errors
}

export const isLtvAdjustments = (fields: string[]) => {
  return fields.includes('ltvAdjustment') || fields.includes('aivLtvAdjustment')
}

export const isEmptyPrices = (prices: IPrice[] = []) => {
  let rlt = true

  prices.forEach((item) => {
    if (!!item.Programs.length) rlt = false
  })

  return rlt
}

export const getRateSpreadExitFeeTooltip = (loan: ILoan, data: IPrice_Program_Price) => {
  const rateSpread = removeComma(data?.RateSpread as number)
  const exitFee = removeComma(data?.ExitFee as number)
  const { proposedLoanAmount } = analyzeLoanStructureValue(loan)

  return {
    rateSpread: `$${getPrice2decimal((proposedLoanAmount * rateSpread) / 100 / 12, false, true)}/month`,
    exitFee: `$${getPrice2decimal((proposedLoanAmount * exitFee) / 100, false, true)}`,
  }
}

export const calcInitialLoanAmount = (loan: ILoan, isTotalLoanAmount: boolean = false) => {
  const definedVisibleFields = visibleLoansLogic(loan)
  let { proposedLoanAmount, constructionReserve } = analyzeLoanStructureValue(loan)
  if (isTotalLoanAmount) proposedLoanAmount = removeComma(loan.proposedLoanAmount)
  let rlt = proposedLoanAmount
  if (definedVisibleFields.includes('constructionReserve')) rlt -= removeComma(constructionReserve)

  if (rlt <= 0) return '0.00'
  return getPrice3decimal(rlt.toFixed(2))
}

export const updateProduct = (products: IProduct[], prices: IPrice[], lockDays: ILockDay[], newData: IProduct) => {
  const newProducts = cloneDeep(products)
  const newPrices = cloneDeep(prices)
  const newLockDays = cloneDeep(lockDays)

  const state = store.getState()
  const loan = state.loan as ILoan

  newProducts.forEach((item) => {
    if (item.Id === newData.Id) {
      item.Description = newData.Description
    }
  })
  newPrices.forEach((item) => {
    if (item.ProductID === newData.Id) {
      item.ProductName = newData.Description

      if (!isNonDSCRProduct(loan.productType))
        item.Programs.forEach((prog) => {
          prog.Prices.forEach((price) => {
            const pitis = combinedMonthlyExpense(loan, { rate: price.Rate, type: item.ProductName })
            price.Dscr = pitis.dscr
          })
        })
    }
  })

  return { newProducts, newPrices, newLockDays }
}

export const addNewProduct = (
  products: IProduct[],
  prices: IPrice[],
  programs: IProgram[],
  lockDays: ILockDay[],
  data: IManualPricingSetting,
) => {
  const newProducts = cloneDeep(products)
  const newPrices = cloneDeep(prices)
  const newPrograms = cloneDeep(programs)
  const newLockDays = cloneDeep(lockDays)

  const { loan, loanDetail } = store.getState()
  const globalSetting = loanDetail.loanGlobalSettings as ILoanGlobalSettings

  const isExist = newProducts.find((item) => item.Description === data.productName)
  if (isExist) {
    toast(`The same product already exists`, { type: 'error' })
    return { newProducts, newPrices, newPrograms, newLockDays }
  }

  const newProduct: IProduct = {
    Id: 0,
    Description: data.productName,
  }
  if (!!newProducts.length) {
    const maxIdProduct = newProducts.reduce((maxProduct, currentProduct) => {
      return currentProduct.Id > maxProduct.Id ? currentProduct : maxProduct
    })
    newProduct.Id = maxIdProduct.Id + 1
  } else newProduct.Id = 0
  newProducts.push(newProduct)

  data.programs.forEach((cofig, index) => {
    const pitis = !isNonDSCRProduct(loan.productType)
      ? combinedMonthlyExpense(loan, { rate: cofig.rate, type: data.productName })
      : undefined

    const newProgram: IProgram = {
      ID: 0,
      Name: cofig.programName,
      PriceOption: globalSetting.priceDisplay || 'Price',
      ManualID: cofig.id,
    }

    const programExist = newPrograms.find((item) => item.Name === newProgram.Name)
    if (programExist) newProgram.ID = programExist.ID
    else {
      if (!!newPrograms.length) {
        const maxIdProgram = newPrograms.reduce((maxProgram, currentProgram) => {
          return currentProgram.ID > maxProgram.ID ? currentProgram : maxProgram
        })
        newProgram.ID = maxIdProgram.ID + 1
      } else newProgram.ID = 0
      newPrograms.push(newProgram)
    }

    const lockTermPrices: ILockTermPrice[] = []
    if (!newLockDays.length)
      newLockDays.push({
        description: String(data.lockTerm),
      })

    newLockDays.forEach((lock) => {
      lockTermPrices.push({
        LockDay: lock.description,
        BasePrice: cofig.price,
        LockTermLLPA: 0,
        Price: String(cofig.price),
      })
    })

    if (index == 0)
      newPrices.push({
        ProductID: newProduct.Id,
        ProductName: newProduct.Description,
        Programs: [
          {
            ProgramID: newProgram.ID,
            ProgramManualID: newProgram.ManualID,
            ProgramName: newProgram.Name,
            Margin: NaN,
            Prices: [
              {
                Rate: cofig.rate,
                BaseRate: cofig.rate,
                Dscr: pitis?.dscr,
                RateSpread: cofig.rateSpread,
                ExitFee: cofig.exitFee,
                LockTermPrices: lockTermPrices,
                CalcHistory: [],
                LLPA: {},
                Limit: {},
              },
            ],
            LLPA: {},
            CalcHistory: [],
            LTV: { from: NaN, to: NaN },
          },
        ],
      })
    else {
      const targetIndex = newPrices.length - 1
      newPrices[targetIndex].Programs.push({
        ProgramID: newProgram.ID,
        ProgramName: newProgram.Name,
        ProgramManualID: newProgram.ManualID,
        Margin: NaN,
        Prices: [
          {
            Rate: cofig.rate,
            BaseRate: cofig.rate,
            Dscr: pitis?.dscr,
            RateSpread: cofig.rateSpread,
            ExitFee: cofig.exitFee,
            LockTermPrices: lockTermPrices,
            CalcHistory: [],
            LLPA: {},
            Limit: {},
          },
        ],
        LLPA: {},
        CalcHistory: [],
        LTV: { from: NaN, to: NaN },
      })
    }
  })

  return { newProducts, newPrices, newPrograms, newLockDays }
}

export const deleteProduct = (products: IProduct[], programs: IProgram[], prices: IPrice[], product: IProduct) => {
  const newProducts = cloneDeep(products)
  const newPrices = cloneDeep(prices)
  const nPrograms = cloneDeep(programs)

  let targetIndex = newProducts.findIndex((item) => item.Id === product.Id)
  newProducts.splice(targetIndex, 1)

  targetIndex = newPrices.findIndex((item) => item.ProductID === product.Id)
  newPrices.splice(targetIndex, 1)

  const requiredIDs: number[] = []
  newPrices.forEach((item) => {
    item.Programs.forEach((v) => {
      requiredIDs.push(v.ProgramID)
    })
  })
  const newPrograms = nPrograms.filter((item) => requiredIDs.includes(item.ID))

  return { newProducts, newPrices, newPrograms }
}

export const addNewProgram = (
  programs: IProgram[],
  prices: IPrice[],
  lockDays: ILockDay[],
  product: IProduct,
  data: IManualProgram[],
) => {
  const newPrograms = cloneDeep(programs)
  const newPrices = cloneDeep(prices)

  const { loan, loanDetail } = store.getState()
  const globalSetting = loanDetail.loanGlobalSettings as ILoanGlobalSettings

  data.forEach((value) => {
    const newProgram: IProgram = {
      ID: 0,
      Name: value.programName,
      PriceOption: globalSetting.priceDisplay || 'Price',
      ManualID: value.id,
    }
    const isExist = newPrograms.find((item) => item.Name === newProgram.Name)
    if (isExist) newProgram.ID = isExist.ID
    else {
      if (!!newPrograms.length) {
        const maxIdProgram = newPrograms.reduce((maxProgram, currentProgram) => {
          return currentProgram.ID > maxProgram.ID ? currentProgram : maxProgram
        })
        newProgram.ID = maxIdProgram.ID + 1
      } else newProgram.ID = 0
      newPrograms.push(newProgram)
    }

    const targetProgramConfig =
      globalSetting.manualPricingSetting.programs.find((item) => item.id === newProgram.ManualID) ??
      globalSetting.manualPricingSetting.programs[0]

    newPrices.forEach((item) => {
      if (item.ProductID === product.Id) {
        const alreadyExist = item.Programs.find((data) => data.ProgramName === newProgram.Name)
        if (alreadyExist) return { newPrograms, newPrices }

        const pitis = !isNonDSCRProduct(loan.productType)
          ? combinedMonthlyExpense(loan, { rate: targetProgramConfig.rate, type: item.ProductName })
          : undefined

        const lockTermPrices: ILockTermPrice[] = []

        lockDays.forEach((lock) => {
          lockTermPrices.push({
            LockDay: lock.description,
            BasePrice: targetProgramConfig.price,
            LockTermLLPA: 0,
            Price: String(targetProgramConfig.price),
          })
        })
        item.Programs.push({
          ProgramID: newProgram.ID,
          ProgramName: newProgram.Name,
          Margin: NaN,
          Prices: [
            {
              Rate: targetProgramConfig.rate,
              BaseRate: targetProgramConfig.rate,
              Dscr: pitis?.dscr,
              RateSpread: targetProgramConfig.rateSpread,
              ExitFee: targetProgramConfig.exitFee,
              LockTermPrices: lockTermPrices,
              CalcHistory: [],
              LLPA: {},
              Limit: {},
            },
          ],
          LLPA: {},
          CalcHistory: [],
          LTV: { from: NaN, to: NaN },
        })
      }
    })
  })

  return { newPrograms, newPrices }
}

export const updateProgram = (programs: IProgram[], prices: IPrice[], program: IProgram) => {
  const newPrograms = cloneDeep(programs)
  const newPrices = cloneDeep(prices)

  newPrograms.forEach((item) => {
    if (item.ID === program.ID) {
      item.Name = program.Name
    }
  })

  newPrices.forEach((item) => {
    item.Programs.forEach((data) => {
      if (data.ProgramID === program.ID) {
        data.ProgramName = program.Name
      }
    })
  })

  return { newPrograms, newPrices }
}

export const deleteProgram = (programs: IProgram[], prices: IPrice[], productID: number, programID: number) => {
  const newPrograms = cloneDeep(programs)
  const newPrices = cloneDeep(prices)

  newPrices.find((item) => {
    if (item.ProductID === productID) {
      const targetIndex = item.Programs.findIndex((prog) => prog.ProgramID === programID)
      item.Programs.splice(targetIndex, 1)
    }
  })

  const removeIndex = programs.findIndex((item) => item.ID === programID)
  newPrograms.splice(removeIndex, 1)

  return { newPrograms, newPrices }
}

export const updateLockDay = (
  program: IProgram,
  prices: IPrice[],
  lockDays: ILockDay[],
  lockDayIndex: number,
  value: number,
  isUpdate: boolean,
) => {
  const newPrices = cloneDeep(prices)
  const newLockDays = cloneDeep(lockDays)

  if (isUpdate) {
    newLockDays[lockDayIndex].description = String(value)

    newPrices.forEach((item) => {
      item.Programs.forEach((data) => {
        data.Prices.forEach((rate) => {
          if (rate.LockTermPrices[lockDayIndex]) rate.LockTermPrices[lockDayIndex].LockDay = String(value)
        })
      })
    })
  } else {
    const { loanDetail } = store.getState()
    const globalSetting = loanDetail.loanGlobalSettings as ILoanGlobalSettings
    const targetProgramConfig =
      globalSetting.manualPricingSetting.programs.find((item) => item.id === program.ManualID) ??
      globalSetting.manualPricingSetting.programs[0]

    value = globalSetting.manualPricingSetting.lockTerm
    newLockDays.push({
      description: String(value),
    })

    newPrices.forEach((item) => {
      item.Programs.forEach((prog) => {
        prog.Prices.forEach((price) => {
          price.LockTermPrices.push({
            LockDay: String(value),
            BasePrice: targetProgramConfig.price,
            LockTermLLPA: 0,
            Price: String(targetProgramConfig.price),
          })
        })
      })
    })
  }

  return { newPrices, newLockDays }
}

export const deleteLockDay = (prices: IPrice[], lockDays: ILockDay[], index: number) => {
  const newPrices = cloneDeep(prices)
  const newLockDays = cloneDeep(lockDays)

  newLockDays.splice(index, 1)
  newPrices.forEach((item) => {
    item.Programs.forEach((prog) => {
      prog.Prices.forEach((price) => {
        price.LockTermPrices.splice(index, 1)
      })
    })
  })

  return { newLockDays, newPrices }
}

export const addRate = (
  program: IProgram,
  prices: IPrice[],
  lockDays: ILockDay[],
  selectedProductId: number,
  selectedProgramId: number,
  index: number,
  value: number,
) => {
  const newPrices = cloneDeep(prices)

  const { loanDetail, loan } = store.getState()
  const globalSetting = loanDetail.loanGlobalSettings as ILoanGlobalSettings
  const targetProgramConfig =
    globalSetting.manualPricingSetting.programs.find((item) => item.id === program.ManualID) ??
    globalSetting.manualPricingSetting.programs[0]

  newPrices.forEach((item) => {
    if (item.ProductID === selectedProductId) {
      item.Programs.forEach((prog) => {
        if (prog.ProgramID === selectedProgramId) {
          if (index === -1) {
            const lockTermPrices: ILockTermPrice[] = []
            lockDays.forEach((lock) => {
              lockTermPrices.push({
                LockDay: lock.description,
                BasePrice: targetProgramConfig.price,
                LockTermLLPA: 0,
                Price: String(targetProgramConfig.price),
              })
            })

            const pitis = !isNonDSCRProduct(loan.productType)
              ? combinedMonthlyExpense(loan, { rate: targetProgramConfig.rate, type: item.ProductName })
              : undefined

            prog.Prices.push({
              Rate: targetProgramConfig.rate,
              BaseRate: targetProgramConfig.rate,
              Dscr: pitis?.dscr,
              RateSpread: targetProgramConfig.rateSpread,
              ExitFee: targetProgramConfig.exitFee,
              LockTermPrices: lockTermPrices,
              CalcHistory: [],
              LLPA: {},
              Limit: {},
            })
          } else {
            prog.Prices[index].Rate = value

            if (!isNonDSCRProduct(loan.productType)) {
              const pitis = combinedMonthlyExpense(loan, { rate: value, type: item.ProductName })
              prog.Prices[index].Dscr = pitis?.dscr
            }
          }
        }
      })
    }
  })

  return { newPrices }
}

export const deleteRate = (prices: IPrice[], selectedProductId: number, selectedProgramId: number, index: number) => {
  const newPrices = cloneDeep(prices)

  newPrices.forEach((item) => {
    if (item.ProductID === selectedProductId) {
      item.Programs.forEach((prog) => {
        if (prog.ProgramID === selectedProgramId) {
          prog.Prices.splice(index, 1)
        }
      })
    }
  })

  return { newPrices }
}

export const updatePrice = (
  prices: IPrice[],
  selectedProductId: number,
  selectedProgramId: number,
  rateIndex: number,
  lockDayIndex: number,
  price: number,
) => {
  const newPrices = cloneDeep(prices)

  newPrices.forEach((item) => {
    if (item.ProductID === selectedProductId) {
      item.Programs.forEach((prog) => {
        if (prog.ProgramID === selectedProgramId) {
          prog.Prices[rateIndex].LockTermPrices[lockDayIndex].BasePrice = price
          prog.Prices[rateIndex].LockTermPrices[lockDayIndex].Price = price.toString()
        }
      })
    }
  })

  return { newPrices }
}

export const updateRateSpreadExitFee = (
  prices: IPrice[],
  selectedProductId: number,
  selectedProgramId: number,
  index: number,
  key: 'RateSpread' | 'ExitFee',
  value: number,
) => {
  const newPrices = cloneDeep(prices)

  newPrices.forEach((item) => {
    if (item.ProductID === selectedProductId) {
      item.Programs.forEach((prog) => {
        if (prog.ProgramID === selectedProgramId) {
          prog.Prices[index][key] = value
        }
      })
    }
  })

  return { newPrices }
}

export const getUWFee = (loan: ILoan, underwritingFees: IUnderwritingFee[], globalSetting: ILoanGlobalSettings) => {
  const { proposedLoanAmount } = analyzeLoanStructureValue(loan)
  const { productType, transactionType, propertyType } = loan

  const targetUWfee = underwritingFees.find(
    (item) =>
      item.productType === productType &&
      item.transactionType === transactionType &&
      item.propertyType === propertyType &&
      item.loanAmountFrom <= proposedLoanAmount &&
      item.loanAmountTo >= proposedLoanAmount,
  )

  if (targetUWfee) return targetUWfee.underwritingFee

  return globalSetting.defaultUnderwritingFee
}

export const validateMinMaxLimitPerTypes = (
  amount: number,
  limit: IMinMaxLimitPerTypes,
  loanAmount: number,
  title: string,
) => {
  const { minAmount, maxAmount, minPercent, maxPercent } = limit

  if (!isEmpty(minAmount) && !isEmpty(maxAmount)) {
    if (amount < minAmount || amount > maxAmount)
      return `${title} should be between $${getPrice3decimal(minAmount)} and $${getPrice3decimal(maxAmount)}`
  } else if (!isEmpty(minAmount) && isEmpty(maxAmount)) {
    if (amount < minAmount) return `${title} should be greater than $${getPrice3decimal(minAmount)}`
  } else if (isEmpty(minAmount) && !isEmpty(maxAmount)) {
    if (amount > maxAmount) return `${title} should be less than $${getPrice3decimal(maxAmount)}`
  }

  if (!isEmpty(minPercent) && !isEmpty(maxPercent)) {
    const minLimit = (loanAmount * minPercent) / 100
    const maxLimit = (loanAmount * maxPercent) / 100
    if (amount < minLimit || amount > maxLimit)
      return `${title} should be between ${getPrice3decimal(minPercent)}% and ${getPrice3decimal(
        maxPercent,
      )}% of Loan Amount`
  } else if (!isEmpty(minPercent) && isEmpty(maxPercent)) {
    const minLimit = (loanAmount * minPercent) / 100
    if (amount < minLimit) return `${title} should be greater than ${getPrice3decimal(minPercent)}% of Loan Amount`
  } else if (isEmpty(minPercent) && !isEmpty(maxPercent)) {
    const maxLimit = (loanAmount * maxPercent) / 100
    if (amount > maxLimit) return `${title} should be less than ${getPrice3decimal(maxPercent)}% of Loan Amount`
  }

  return ''
}

export const convertLoanDetailStatus = (value: IRange) => {
  if (value?.from === undefined) return ''
  if (value.from === null && value.to !== null) return `Max = ${getPrice3decimal(value.to)}`
  else if (value.from !== null && value.to === null) return `Min = ${getPrice3decimal(value.from)}`
  else if (value.from !== null && value.to !== null)
    return `Min = ${getPrice3decimal(value.from)}, Max = ${getPrice3decimal(value.to)}`
  else return ''
}

export const getLoanDetailsValue = (key: string, loan: ILoan, fieldProperties: Record<string, IVisibleProp>) => {
  if (key === 'estimatedCreditScore' && loan.isNoScore) return 'No Score'
  return convertValueToLabel(key, (loan as any)[key], fieldProperties)
}

export const getRangeFromString = (value: string) => {
  if (!value) return { from: null, to: null }
  const valueArr = value.split('-')
  if (valueArr.length !== 2) return { from: null, to: null }
  const rlt = {
    from: valueArr[0] == 'null' ? null : removeComma(valueArr[0]),
    to: valueArr[1] == 'null' ? null : removeComma(valueArr[1]),
  }
  return rlt
}

export const getLoanDetailChanges = (
  originLoanDetail: ILoan | undefined,
  loan: ILoan,
  loanFields: Record<string, InputType>,
  loanProcessData: ILoanProcess,
  changedLoanProcessData: ILoanProcess,
  multiPropertiesFields: Record<string, InputType>[],
  visibleFields: string[],
  propertyVisibleFields: string[][],
) => {
  let rlt: Record<string, ILoanDetailChangedJson> = {}
  if (!originLoanDetail) return rlt
  let ltvChanged = false
  let cltvChanged = false

  Object.keys(loanFields).map((key) => {
    if (!isCustomFieldKey(key) && (originLoanDetail as any)[key] === undefined) return
    if (!visibleFields.includes(key)) return
    let newValue = (loan as any)[key]
    let originValue = (originLoanDetail as any)[key]
    if (loanFields[key].type === 'thousandSep' || loanFields[key].type === 'thousandSepNoDecimal') {
      newValue = isEmpty(newValue) ? null : removeComma(newValue)
      originValue = isEmpty(originValue) ? null : removeComma(originValue)
    } else if (
      loanFields[key].inputType === 'text' &&
      (loanFields[key].type === 'text' || isEmpty(loanFields[key].type))
    ) {
      newValue = isEmpty(newValue) ? null : String(newValue).trim()
      originValue = isEmpty(originValue) ? null : String(originValue).trim()
    }
    if (newValue != originValue) {
      rlt[key] = {
        key,
        label: loanFields[key].title,
        origin: originValue,
        changed: newValue,
      }

      if (key === 'additionalPropertyAddress') {
        try {
          if (JSON.stringify(loan[key]) === JSON.stringify((originLoanDetail as any)[key])) delete rlt[key]
          if (loan[key]?.length === 0 && (originLoanDetail as any)[key]?.length === 0) {
            delete rlt[key]
          }
        } catch {}
      }
      if (calcLTVFields.indexOf(key) !== -1) ltvChanged = true
      if (calcCLTVFields.indexOf(key) !== -1) cltvChanged = true
      if (key === 'subjectPropertyAddress') {
        const key2 = 'propertyCounty'
        if (loan[key2] !== (originLoanDetail as any)[key2]) {
          rlt[key2] = {
            key: 'propertyCounty',
            label: 'Property County',
            origin: (originLoanDetail as any)[key2],
            changed: loan[key2],
          }
        }
      }
      if (dscrRelatedFields.includes(key)) {
        const originDSCR = combinedMonthlyExpense(originLoanDetail, loanProcessData).dscr
        const changedDSCR = combinedMonthlyExpense(loan, changedLoanProcessData).dscr

        if (Number(originDSCR) !== Number(changedDSCR))
          rlt['dscr'] = {
            key: 'dscr',
            label: 'Calculated DSCR',
            origin: originDSCR,
            changed: changedDSCR,
          }
      }
    }
  })
  if (ltvChanged) {
    const _origin = calculateLTV(originLoanDetail)
    const _changed = calculateLTV(loan)
    if (Number(_origin) !== Number(_changed)) {
      rlt['ltv'] = {
        key: 'LTV',
        label: 'LTV',
        origin: _origin,
        changed: _changed,
      }
    }
  }
  if (cltvChanged) {
    const _origin = calculateLTV(originLoanDetail, true)
    const _changed = calculateLTV(loan, true)
    if (Number(_origin) !== Number(_changed)) {
      rlt['cltv'] = {
        key: 'CLTV',
        label: 'CLTV',
        origin: _origin,
        changed: _changed,
      }
    }
  }
  ltvChanged = false
  cltvChanged = false
  if (!originLoanDetail?.multipleProperties || multiPropertiesFields?.length !== loan?.multipleProperties?.length)
    return rlt
  multiPropertiesFields.forEach((item, index) => {
    Object.keys(item).map((key) => {
      if (!propertyVisibleFields[index].includes(key) || !MultiPropertyFields.includes(key)) return
      let newValue = (loan.multipleProperties?.[index] as any)?.[key] ?? ''
      let originValue = (originLoanDetail.multipleProperties?.[index] as any)?.[key] ?? ''
      if (item[key].type === 'thousandSep' || item[key].type === 'thousandSepNoDecimal') {
        newValue = removeComma(newValue)
        originValue = isEmpty(originValue) ? null : removeComma(originValue)
      } else if (item[key].inputType === 'text' && (item[key].type === 'text' || isEmpty(item[key].type))) {
        newValue = String(newValue).trim()
        originValue = isEmpty(originValue) ? null : String(originValue).trim()
      }
      if (newValue != originValue) {
        if (calcLTVFields.indexOf(key) !== -1) ltvChanged = true
        if (calcCLTVFields.indexOf(key) !== -1) cltvChanged = true
      }
    })
  })
  if (ltvChanged) {
    const _origin = calculateLTV(originLoanDetail)
    const _changed = calculateLTV(loan)
    if (Number(_origin) !== Number(_changed)) {
      rlt['ltv'] = {
        key: 'LTV',
        label: 'LTV',
        origin: _origin,
        changed: _changed,
      }
    }
  }
  if (cltvChanged) {
    const _origin = calculateLTV(originLoanDetail, true)
    const _changed = calculateLTV(loan, true)
    if (Number(_origin) !== Number(_changed)) {
      rlt['cltv'] = {
        key: 'CLTV',
        label: 'CLTV',
        origin: _origin,
        changed: _changed,
      }
    }
  }
  return rlt
}

export const getPropertyDetailChanges = (
  originLoanDetail: ILoan | undefined,
  loan: ILoan,
  multiPropertiesFields: Record<string, InputType>[],
  propertyVisibleFields: string[][],
  loanProcessData: ILoanProcess,
  changedLoanProcessData: ILoanProcess,
  isMultipleProperties: boolean,
) => {
  let rlt: IPropertyChangedJson[] = []
  if (!isMultipleProperties) return rlt
  if (!originLoanDetail?.multipleProperties || multiPropertiesFields?.length !== loan?.multipleProperties?.length)
    return rlt
  multiPropertiesFields.forEach((item, index) => {
    Object.keys(item).map((key) => {
      if (!propertyVisibleFields[index].includes(key) || !MultiPropertyFields.includes(key)) return
      let newValue = (loan.multipleProperties?.[index] as any)?.[key] ?? ''
      let originValue = (originLoanDetail.multipleProperties?.[index] as any)?.[key] ?? ''
      if (item[key].type === 'thousandSep' || item[key].type === 'thousandSepNoDecimal') {
        newValue = isEmpty(newValue) ? null : removeComma(newValue)
        originValue = isEmpty(originValue) ? null : removeComma(originValue)
      } else if (item[key].inputType === 'text' && (item[key].type === 'text' || isEmpty(item[key].type))) {
        newValue = isEmpty(newValue) ? null : String(newValue).trim()
        originValue = isEmpty(originValue) ? null : String(originValue).trim()
      }
      if (newValue != originValue) {
        rlt.push({
          id: loan.multipleProperties[index].id,
          index,
          key,
          label: item[key].title,
          origin: originValue,
          changed: newValue,
        })

        if (dscrRelatedFields.includes(key)) {
          const originDSCR = !!originLoanDetail?.multipleProperties?.[index]
            ? combinedPropertyMonthlyExpense(
                originLoanDetail,
                originLoanDetail.multipleProperties[index],
                loanProcessData,
              ).dscr
            : ''
          const changedDSCR = combinedPropertyMonthlyExpense(
            loan,
            loan.multipleProperties[index],
            changedLoanProcessData,
          ).dscr
          if (Number(originDSCR) !== Number(changedDSCR)) {
            const dscrIndex = rlt.findIndex(
              (item) => item.id === loan.multipleProperties[index].id && item.key === 'dscr',
            )
            if (dscrIndex === -1)
              rlt.push({
                id: loan.multipleProperties[index].id,
                index,
                key: 'dscr',
                label: 'Calculated DSCR',
                origin: originDSCR,
                changed: changedDSCR,
              })
            else
              rlt[dscrIndex] = {
                id: loan.multipleProperties[index].id,
                index,
                key: 'dscr',
                label: 'Calculated DSCR',
                origin: originDSCR,
                changed: changedDSCR,
              }
          }
        }
      }
    })
  })
  return rlt
}

export const changePoolLoanField = (
  key: string,
  value: any,
  importedLoans: Record<string, any>[],
  selectedLoan: string,
  loanFields: Record<string, InputType>,
  loanDetail: ILoanDetail,
) => {
  const newLoans = cloneDeep(importedLoans)
  const index = importedLoans.findIndex((item) => item['Loan #'] == selectedLoan)
  newLoans[index][loanFields[key].title] = value

  if (key === 'transactionType' && value === 'Refinance')
    newLoans[index][loanFields['propertyPurchasePrice'].title] = null
  if (key === 'experience') {
    const { from, to } = getRangeFromString(value)
    if (from === to && from !== null) newLoans[index][loanFields['howManyExperiences'].title] = from
  }
  if (key === 'rehabBudget') {
    const { minTotalConstructionFinanced } = loanDetail.loanGlobalSettings
    if (minTotalConstructionFinanced === 100)
      newLoans[index][loanFields['constructionReserve'].title] = removeComma(value)
  }
  if (key === 'productType' && isNonDSCRProduct(value))
    newLoans[index][loanFields['amortizationType'].title] = 'IO - Fixed'
  if (key === 'propertyType' && ['SFR-Detached', 'SFR-Attached', 'Industrial'].includes(value))
    newLoans[index][loanFields['numberOfUnits'].title] = 1

  return newLoans
}

export const getCustomRange = (value: string) => {
  if (value.toUpperCase() === 'N') return `null-null`
  if (value.includes('+')) return `${removeComma(value)}-null`
  if (value.includes('<=')) return `null-${removeComma(value)}`
  if (value.split('-').length === 1) return `${removeComma(value)}-${removeComma(value)}`
  else return value
}

export const getBankruptcyRange = (value: string) => {
  if (value.toUpperCase() === 'N') return `null-null-null`
  if (value.toLowerCase() === 'active') return `0-null-null`
  if (value.includes('+')) {
    const arr = value.split('-')
    return `${arr[0]}-${removeComma(arr[1])}-null`
  }
  if (value.includes('<=')) {
    const arr = value.split('-')
    return `${arr[0]}-null-${removeComma(arr[1])}`
  }
  const arr = value.split('-')
  return `${arr[0]}-${arr[1]}-${arr[2]}`
}

export const canLockRateByLoanStatus = (
  loanDetail: ILoanDetail,
  lockRateRestrictions: any,
  key: 'initialLock' | 'relock' | 'lockExtension',
) => {
  const { loanStatus } = loanDetail
  return lockRateRestrictions?.[key]?.loanStatus?.[loanStatus] !== false
}

export const validateLoanDetailForPricing = (
  loan: ILoan,
  loanFields: Record<string, InputType>,
  multiPropertiesFields: Record<string, InputType>[],
  loanDetail: ILoanDetail,
  priceAdjustmentObject: any,
  visibleFields: string[],
  propertyVisibleFields: string[][],
  isMultipleProperties: boolean,
) => {
  const {
    minTotalConstructionFinanced,
    maxTotalConstructionFinanced,
    minConstructionFinancedHoldback,
    maxConstructionFinancedHoldback,
  } = loanDetail.loanGlobalSettings
  let flag = true

  visibleFields.map((key) => {
    if (priceLoanRelatedFields.includes(key)) {
      if (loanFields[key]?.error?.length) {
        if (priceAdjustmentObject[key] !== undefined && !isEmpty((loan as any)[key])) {
        } else flag = false
      }
    }
  })
  if (isMultipleProperties)
    propertyVisibleFields.forEach((item, index) => {
      item.forEach((key) => {
        if (MultiPropertyFields.includes(key) && key !== 'estimatedDscr')
          if (priceLoanRelatedFields.includes(key)) {
            if (multiPropertiesFields[index][key]?.error?.length) {
              if (priceAdjustmentObject[key] !== undefined && !isEmpty((loan.multipleProperties[index] as any)[key])) {
              } else flag = false
            }
          }
      })
    })
  const { closingError, rehabFinancedPercent, totalRehabFinancedPercent } = estimatedClosingAmountBorrower(
    loan,
    loanDetail.loanGlobalSettings,
  )
  if (isEmpty(priceAdjustmentObject['atClosingBorrowerAmount']) && closingError.length) flag = false
  if (
    isEmpty(priceAdjustmentObject['constructionFinancedHoldback']) &&
    removeComma(rehabFinancedPercent) > removeComma(maxConstructionFinancedHoldback)
  )
    flag = false
  if (
    isEmpty(priceAdjustmentObject['constructionFinancedHoldback']) &&
    removeComma(rehabFinancedPercent) > 0 &&
    removeComma(rehabFinancedPercent) < removeComma(minConstructionFinancedHoldback)
  )
    flag = false
  if (
    isEmpty(priceAdjustmentObject['totalRehabFinancedPercent']) &&
    removeComma(totalRehabFinancedPercent) > removeComma(maxTotalConstructionFinanced)
  )
    flag = false
  if (
    isEmpty(priceAdjustmentObject['totalRehabFinancedPercent']) &&
    removeComma(totalRehabFinancedPercent) > 0 &&
    removeComma(totalRehabFinancedPercent) < removeComma(minTotalConstructionFinanced)
  )
    flag = false
  if (isMultipleProperties && loan.howManyProperties != (loan?.multipleProperties?.length || 0) + 1) flag = false
  return flag
}

export const validateLoanDetailForSaveToPipeline = (
  loan: ILoan,
  loanFields: Record<string, InputType>,
  multiPropertiesFields: Record<string, InputType>[],
  visibleFields: string[],
  propertyVisibleFields: string[][],
  isMultipleProperties: boolean,
) => {
  let flag = true
  visibleFields.map((key) => {
    if (!!loanFields[key]?.error?.length && loanFields[key].error === REQUIRED_FIELD_ERROR_MESSAGE) flag = false
  })
  if (isMultipleProperties) {
    if (loan.howManyProperties != (loan?.multipleProperties?.length || 0) + 1) flag = false
    propertyVisibleFields.forEach((item, index) => {
      item.forEach((key) => {
        if (MultiPropertyFields.includes(key) && key !== 'estimatedDscr')
          if (
            !!multiPropertiesFields[index][key]?.error?.length &&
            multiPropertiesFields[index][key].error === REQUIRED_FIELD_ERROR_MESSAGE
          )
            flag = false
      })
    })
  }
  return flag
}
